import { useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, ArrowLeft, RefreshCw, Settings, Sparkles, X } from "lucide-react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import AIResult from "@/components/ai/AIResult";
import AIProgress from "@/components/ai/AIProgress";
import AIThemeOptions, { THEME_DOMAINS, ALL_THEME_DOMAINS } from "@/components/ai/AIThemeOptions";
import AILogoOptions from "@/components/ai/AILogoOptions";
import { groupByCategory, iconFor } from "@/lib/aiActions";

const Lbl = ({ children }) => (
  <label className="text-[11px] uppercase tracking-[0.15em] text-ash">{children}</label>
);

const EXTRA_LABELS = {
  tone: "Ton", length: "Longueur", style: "Style", color: "Couleur dominante",
  business: "Activité de la boutique",
};

/**
 * Modale de l'assistant IA.
 *
 * Trois temps : choisir l'action, décrire le contexte, juger le résultat.
 * L'étape de choix est sautée quand la page n'autorise qu'une seule action —
 * inutile de faire cliquer sur une liste d'un élément.
 *
 * En cas d'échec, le message du fournisseur s'affiche dans la modale et
 * l'utilisateur réessaie SANS la fermer (§18) : il ne doit jamais avoir à
 * ressaisir son contexte à cause d'un quota dépassé.
 */
export default function AIAssistantModal({ request, onApply, onCancel }) {
  const [actions, setActions] = useState(null);
  const [actionKey, setActionKey] = useState(request.action || null);
  const [context, setContext] = useState("");
  const [extras, setExtras] = useState({});
  const [domains, setDomains] = useState(ALL_THEME_DOMAINS);
  const [result, setResult] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  // Second temps d'un logo : les déclinaisons se fabriquent à la validation
  // du style, pas à chaque proposition (voir /admin/ai/logo/derivatives).
  const [deriving, setDeriving] = useState(false);
  // Options cochées (registre du serveur, action.options) et retouche en
  // cours d'un logo choisi.
  const [logoOptions, setLogoOptions] = useState([]);
  const [editingLogo, setEditingLogo] = useState(false);
  const [error, setError] = useState(null);
  // État des clés IA : `settings.profiles` décrit les deux profils de clé
  // ("design" pour logos, palettes et thèmes ; "content" pour le reste).
  const [settings, setSettings] = useState(null);
  const abort = useRef(null);

  // Échap ferme la modale ; le focus part sur la zone de contexte pour que
  // l'on puisse écrire immédiatement.
  const contextRef = useRef(null);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onCancel(); };
    window.addEventListener("keydown", onKey);
    // Sans ça, la page continue de défiler derrière la modale sur mobile —
    // on perd la position de lecture en la fermant.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
      if (abort.current) abort.current.abort();
    };
  }, [onCancel]);

  useEffect(() => {
    api.get("/admin/ai/settings")
      .then(({ data }) => setSettings(data || {}))
      .catch(() => setSettings({ ready: false }));

    // Quand la page force une action, le filtre par entité n'a pas lieu
    // d'être : il pourrait écarter l'action demandée et laisser la modale
    // afficher « aucune action disponible » sur une demande explicite.
    api.get("/admin/ai/actions", {
      params: request.entity && !request.action ? { entity: request.entity } : {},
    })
      .then(({ data }) => {
        const allowed = request.allowedActions;
        const list = allowed
          ? (data.actions || []).filter((a) => allowed.includes(a.key))
          : (data.actions || []);
        setActions(list);
        if (!request.action && list.length === 1) setActionKey(list[0].key);
      })
      .catch(() => setActions([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const action = useMemo(
    () => (actions || []).find((a) => a.key === actionKey) || null,
    [actions, actionKey],
  );

  // Prête = la clé DU PROFIL de l'action choisie est active. Avant le choix
  // d'une action, on n'alerte que si AUCUN profil n'est utilisable. Un
  // serveur plus ancien, sans `profiles`, garde le comportement d'origine.
  const ready = (() => {
    if (settings === null) return null;
    const profiles = settings.profiles;
    if (!profiles) return !!settings.ready;
    if (action) return !!profiles[action.key_usage || "content"]?.ready;
    return Object.values(profiles).some((p) => p?.ready);
  })();
  const readinessLabel = action && settings?.profiles?.[action.key_usage || "content"]?.label;

  useEffect(() => {
    if (action && contextRef.current) contextRef.current.focus();
  }, [action]);

  // Le choix des domaines n'a de sens que pour une action qui produit un
  // thème : ailleurs, `fields` garde son sens d'origine (clés d'un JSON
  // libre, écran SEO) et ne doit surtout pas être écrasé.
  const isThemeAction = action?.output_type === "theme";
  const progressSteps = useMemo(() => {
    if (!isThemeAction) return [];
    return THEME_DOMAINS.filter((d) => domains.includes(d.key)).map((d) => d.label.toLowerCase());
  }, [isThemeAction, domains]);

  // Une variable obligatoire de l'action (ex : le texte à améliorer) est
  // fournie par la PAGE, pas saisie ici — la modale ne la redemande donc pas.
  const missingFromPage = useMemo(() => {
    if (!action) return [];
    return (action.required_vars || []).filter(
      (v) => v !== "context" && !String(request.data?.[v] || "").trim(),
    );
  }, [action, request.data]);

  const generate = async () => {
    if (!action) return;
    if (!context.trim()) {
      setError("Décrivez d'abord ce que vous attendez.");
      return;
    }
    setLoading(true);
    setError(null);
    abort.current = new AbortController();
    try {
      const { data } = await api.post("/admin/ai/generate", {
        action: action.key,
        context: {
          entity: request.entity,
          data: {
            ...request.data, ...extras, context: context.trim(),
            ...(action.options?.length ? { logo_options: logoOptions } : {}),
          },
        },
        // Pour un thème, `fields` porte les DOMAINES à composer : le serveur
        // n'exige alors que ceux-là dans le schéma envoyé au modèle (voir
        // outputs.theme_domains). Ailleurs, il garde son sens d'origine.
        fields: isThemeAction ? domains : (request.fields || undefined),
      }, { signal: abort.current.signal });
      setResult(data.result);
      setMeta(data.meta);
    } catch (err) {
      if (err.code === "ERR_CANCELED") return;
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
      abort.current = null;
    }
  };

  const cancelRun = () => {
    if (abort.current) abort.current.abort();
    setLoading(false);
  };

  const isLogo = result?.type === "logo";

  // Retouche du logo choisi : une NOUVELLE version, ajoutée à la suite et
  // sélectionnée. Les déclinaisons éventuelles sont oubliées : elles
  // appartenaient à une autre version.
  const editLogo = async (instruction, options) => {
    const source = (result?.images || [])[result?.chosen ?? 0];
    if (!source?.path) return false;
    setEditingLogo(true);
    setError(null);
    try {
      const { data } = await api.post("/admin/ai/generate", {
        action: "edit_logo",
        context: {
          entity: request.entity,
          data: { ...request.data, instruction, source_path: source.path, logo_options: options || [] },
        },
      });
      const added = data?.result?.images || [];
      if (!added.length) return false;
      setResult((prev) => {
        const images = [...(prev.images || []), ...added];
        return {
          ...prev,
          images,
          chosen: images.length - 1,
          url: added[added.length - 1].url,
          variants: [],
          slots: {},
          pending_variants: true,
          notices: data.result.notices || [],
        };
      });
      setMeta(data.meta);
      return true;
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
      return false;
    } finally {
      setEditingLogo(false);
    }
  };
  const needsDerivatives = isLogo && !(result.variants || []).length;

  // Un logo est d'abord une simple proposition. « Valider ce style » produit
  // alors ses déclinaisons (favicon, icônes, images de partage) et ce n'est
  // qu'avec elles que la page reçoit le résultat — les écrans qui
  // l'appliquent (Studio de thème, médiathèque) attendent `variants`/`slots`.
  const apply = async () => {
    if (!needsDerivatives) {
      onApply(result);
      return;
    }
    const chosen = (result.images || [])[result.chosen ?? 0];
    if (!chosen?.path) {
      onApply(result);
      return;
    }
    setDeriving(true);
    setError(null);
    try {
      const { data } = await api.post("/admin/ai/logo/derivatives", { path: chosen.path });
      const full = { ...result, variants: data.variants || [], slots: data.slots || {}, pending_variants: false };
      setResult(full);
      onApply(full);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setDeriving(false);
    }
  };

  // Les résultats « à choisir » (suggestions, images multiples) ne sont
  // applicables qu'une fois une option retenue — sinon on ne saurait pas
  // laquelle renvoyer à la page.
  const applicable = (() => {
    if (!result) return false;
    if (result.type === "suggestions") return result.chosen !== null && result.chosen !== undefined;
    if (result.type === "logo") {
      return (result.images || []).length === 1 || (result.chosen !== null && result.chosen !== undefined);
    }
    return true;
  })();

  const groups = groupByCategory(actions || []);

  return (
    <div
      className="fixed inset-0 z-[90] flex items-end justify-center sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Assistant IA"
      data-testid="ai-modal"
    >
      <div className="absolute inset-0 bg-ink/50 backdrop-blur-sm" onClick={onCancel} />

      <div className="relative w-full sm:max-w-xl bg-cream rounded-t-2xl sm:rounded-lg shadow-2xl border border-stone max-h-[92dvh] sm:max-h-[90vh] flex flex-col">
        {/* En-tête.
            Sur un écran bas (téléphone en paysage, ou clavier virtuel
            ouvert), l'en-tête et le pied consommaient 165 px des 375 px
            disponibles : il ne restait pas de quoi lire le formulaire. On
            resserre les marges verticales dans ce cas précis, sans toucher à
            l'aération en usage normal. */}
        <div className="flex items-start justify-between gap-3 p-4 sm:p-5 [@media(max-height:480px)]:py-2.5 border-b border-stone shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <span className="h-9 w-9 rounded-lg bg-copper/15 text-copper flex items-center justify-center shrink-0">
              <Sparkles className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <p className="font-serif text-lg sm:text-xl text-ink truncate">
                {request.title || action?.label || "Assistant IA"}
              </p>
              {/* La description explique ce que l'action va faire : la couper
                  à « Crée un logo à partir de votre a… » sur un téléphone
                  vide la phrase de son sens. Elle s'enroule sur deux lignes
                  en petite largeur, et reste sur une seule au-delà. */}
              {action && <p className="text-xs text-ash line-clamp-2 sm:truncate">{action.description}</p>}
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Fermer"
            data-testid="ai-modal-close"
            className="text-ash hover:text-ink p-1 -mr-1 shrink-0"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Corps */}
        <div className="p-4 sm:p-5 space-y-4 overflow-y-auto overscroll-contain">
          {ready === false && (
            <div className="flex items-start gap-3 bg-sand border border-stone rounded-sm p-3 text-sm text-graphite">
              <AlertCircle className="h-4 w-4 text-copper shrink-0 mt-0.5" />
              <p>
                {readinessLabel
                  ? <>La clé IA « {readinessLabel} » n&apos;est pas active pour cette boutique.{" "}</>
                  : <>Aucune clé IA n&apos;est active pour cette boutique.{" "}</>}
                <Link to="/admin/reglages/ia" className="inline-flex items-center gap-1 text-copper hover:text-ink">
                  <Settings className="h-3 w-3" /> Configurer
                </Link>
              </p>
            </div>
          )}

          {/* Étape 1 — choix de l'action */}
          {!action && (
            <div className="space-y-4" data-testid="ai-action-picker">
              {actions === null ? (
                <div className="h-24 bg-white border border-stone rounded-sm animate-pulse" />
              ) : actions.length === 0 ? (
                <p className="text-sm text-graphite">Aucune action disponible dans ce contexte.</p>
              ) : (
                groups.map((group) => (
                  <div key={group.key} className="space-y-1.5">
                    <Lbl>{group.label}</Lbl>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {group.actions.map((a) => {
                        const Icon = iconFor(a.icon);
                        return (
                          <button
                            key={a.key}
                            type="button"
                            data-testid={`ai-action-${a.key}`}
                            onClick={() => setActionKey(a.key)}
                            className="flex items-start gap-2.5 text-left p-3 bg-white border border-stone rounded-sm hover:border-copper transition-colors"
                          >
                            <Icon className="h-4 w-4 text-copper shrink-0 mt-0.5" />
                            <span className="min-w-0">
                              <span className="block text-sm text-ink">{a.label}</span>
                              <span className="block text-[11px] text-ash">{a.description}</span>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Étape 2 — contexte */}
          {action && !result && (
            <div className="space-y-4">
              {missingFromPage.length > 0 && (
                <div className="flex items-start gap-3 bg-sand border border-stone rounded-sm p-3 text-sm text-graphite">
                  <AlertCircle className="h-4 w-4 text-copper shrink-0 mt-0.5" />
                  <p>
                    Cette action a besoin d&apos;un contenu existant ({missingFromPage.join(", ")}), absent de cette page.
                    Choisissez une autre action.
                  </p>
                </div>
              )}

              {action.mutates_storefront && (
                <div className="flex items-start gap-3 bg-sand border border-stone rounded-sm p-3 text-sm text-graphite">
                  <AlertCircle className="h-4 w-4 text-copper shrink-0 mt-0.5" />
                  <p>
                    Cette action modifie l&apos;apparence de la vitrine. Le résultat s&apos;applique à
                    l&apos;éditeur : rien n&apos;est publié tant que vous n&apos;avez pas enregistré.
                  </p>
                </div>
              )}

              <div>
                <Lbl>Que voulez-vous obtenir ?</Lbl>
                <Textarea
                  ref={contextRef}
                  data-testid="ai-context"
                  value={context}
                  onChange={(e) => { setContext(e.target.value); setError(null); }}
                  rows={4}
                  maxLength={4000}
                  // Exemple volontairement court : sur un écran de 320 px, le
                  // texte d'aide précédent occupait cinq lignes dans une zone
                  // qui n'en affiche que quatre — la dernière était coupée en
                  // plein milieu des lettres.
                  placeholder="Ex : boutique de luminaires design à Lyon, clientèle particuliers, livraison en 48 h."
                  className="mt-1.5 rounded-sm border-stone bg-white text-base md:text-sm"
                />
                <p className="text-[11px] text-ash mt-1">
                  Plus le contexte est précis (activité, cible, ton, arguments), plus la proposition est pertinente.
                </p>
              </div>

              {isThemeAction && <AIThemeOptions value={domains} onChange={setDomains} />}

              {!!action.options?.length && (
                <AILogoOptions options={action.options} value={logoOptions} onChange={setLogoOptions} />
              )}

              {/* Une variable déjà fournie par la page (le libellé de la
                  page SEO, les valeurs actuelles…) ne doit pas être
                  redemandée : seules restent les options que l'utilisateur
                  peut réellement vouloir régler, comme le ton. */}
              {(action.optional_vars || [])
                .filter((v) => v !== "context" && !(v in (request.data || {})))
                .map((name) => (
                  <div key={name}>
                    <Lbl>{EXTRA_LABELS[name] || name}</Lbl>
                    <Input
                      data-testid={`ai-extra-${name}`}
                      value={extras[name] || ""}
                      onChange={(e) => setExtras((x) => ({ ...x, [name]: e.target.value }))}
                      placeholder="Facultatif"
                      className="mt-1.5 rounded-sm border-stone bg-white text-base md:text-sm"
                    />
                  </div>
                ))}
            </div>
          )}

          {loading && (
            <AIProgress
              steps={progressSteps}
              label={isThemeAction ? "Composition du thème" : "Génération en cours"}
            />
          )}

          {/* Étape 3 — résultat */}
          {result && (
            <AIResult
              result={result}
              onChange={setResult}
              {...(isLogo ? {
                onEditLogo: editLogo,
                editingLogo,
                logoOptions: action?.options,
              } : {})}
            />
          )}

          {error && (
            <div
              className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-sm p-3 text-sm text-red-800"
              data-testid="ai-error"
              role="alert"
            >
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <p>{error}</p>
            </div>
          )}

          {meta && result && (
            <p className="text-[11px] text-ash">
              {meta.model} · {(meta.duration_ms / 1000).toFixed(1)} s
              {meta.tokens_out ? ` · ${meta.tokens_out} jetons` : ""}
            </p>
          )}
        </div>

        {/* Pied */}
        <div className="flex flex-col-reverse sm:flex-row sm:flex-wrap sm:items-center gap-2 p-4 sm:p-5 [@media(max-height:480px)]:py-2.5 border-t border-stone shrink-0">
          {action && !request.action && (
            <button
              type="button"
              data-testid="ai-back"
              onClick={() => { setActionKey(null); setResult(null); setError(null); }}
              className="flex items-center justify-center sm:justify-start gap-1.5 text-xs uppercase tracking-[0.12em] text-ash hover:text-ink px-2 py-2.5 w-full sm:w-auto"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Actions
            </button>
          )}

          <div className="hidden sm:block sm:flex-1" />

          {loading ? (
            <button
              type="button"
              data-testid="ai-cancel-run"
              onClick={cancelRun}
              className="border border-stone px-5 py-3 sm:py-2.5 uppercase tracking-[0.12em] text-xs text-graphite hover:text-ink w-full sm:w-auto"
            >
              Annuler la génération
            </button>
          ) : result ? (
            <>
              <button
                type="button"
                data-testid="ai-regenerate"
                disabled={deriving || editingLogo}
                onClick={() => { setResult(null); setMeta(null); generate(); }}
                className="flex items-center justify-center gap-2 border border-stone px-5 py-3 sm:py-2.5 uppercase tracking-[0.12em] text-xs text-graphite hover:text-ink w-full sm:w-auto disabled:opacity-50"
              >
                <RefreshCw className="h-3.5 w-3.5" /> {isLogo ? "Autre proposition" : "Régénérer"}
              </button>
              <button
                type="button"
                data-testid="ai-apply"
                disabled={!applicable || deriving || editingLogo}
                onClick={apply}
                className="bg-ink text-cream px-6 py-3 sm:py-2.5 uppercase tracking-[0.12em] text-xs hover:bg-gold hover:text-ink transition-colors disabled:opacity-50 w-full sm:w-auto"
              >
                {deriving ? "Création des déclinaisons…" : needsDerivatives ? "Valider ce style" : "Appliquer"}
              </button>
            </>
          ) : action ? (
            <button
              type="button"
              data-testid="ai-generate"
              onClick={generate}
              disabled={ready === false || missingFromPage.length > 0}
              className="flex items-center justify-center gap-2 bg-copper text-cream px-6 py-3 sm:py-2.5 uppercase tracking-[0.12em] text-xs hover:bg-ink transition-colors disabled:opacity-50 w-full sm:w-auto"
            >
              <Sparkles className="h-4 w-4" /> Générer
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
